import { FileSortOrder } from '@/types/node';
import { clampValue } from '@/utils/numbers';
import { BaseFileView } from './BaseFileView';
import type { ImportData } from '@/types/node';
import type { FileKind, NodeId, Web, Edit, CameraMode, Dimensions } from 'web';
import type { FlowGraph } from '../FlowGraph';

const MIN_DIMENSION = 1;
const MAX_DIMENSION = 8192;
const DEFAULT_DIMENSION = 1024;

export class ImportView extends BaseFileView<ImportData> {
  constructor(graph: FlowGraph, nodeId: NodeId) {
    super(graph, nodeId);
  }

  getNodeData(): ImportData {
    return this.graph.store.getNodeData<ImportData>(this.nodeId);
  }

  getFilesList(): FileKind[] {
    return this.getNodeData().files;
  }

  protected wasmSetFiles(wasm: Web, files: FileKind[]): Edit {
    return wasm.setFiles(this.nodeId, files);
  }

  /**
   * Gets the currently selected file
   */
  getCurrentFile(): FileKind | null {
    const files = this.getFilesList();
    const selectedIndex = this.getSelectedIndex();
    return files[selectedIndex] ?? null;
  }

  renameCurrentFile(name: string) {
    const files = this.getFilesList();
    const index = this.getSelectedIndex();
    if (!files[index]) return;

    const sortOrder = this.getNodeData().sortOrder;
    if (sortOrder === FileSortOrder.Name) {
      const ensured = this.ensureInsertionOrders(files);
      const renamedFile = { ...ensured[index], name };
      const updatedFiles = ensured.map((file, i) => (i === index ? renamedFile : file));
      const sorted = this.sortFilesByName(updatedFiles);
      const newIndex = sorted.indexOf(renamedFile);
      this.setFiles(sorted, newIndex, false);
    } else {
      const updatedFiles = files.map((file, i) => (i === index ? { ...file, name } : file));
      this.graph.wasmEdit((wasm) => this.wasmSetFiles(wasm, updatedFiles));
    }
  }

  setCameraLocked(locked: boolean) {
    this.graph.wasmEdit((wasm) => wasm.setCameraLocked(this.nodeId, locked));
  }

  setCameraMode(mode: CameraMode) {
    this.graph.wasm.call((wasm) => wasm.setCameraMode(this.nodeId, mode));
  }

  setShowGrid(showGrid: boolean) {
    this.graph.wasm.call((wasm) => wasm.setShowGrid(this.nodeId, showGrid));
  }

  setFov(fov: number, ongoing = false) {
    const clampedFov = Math.min(Math.max(fov, 1), 179);
    const currentFile = this.getCurrentFile();

    if (
      currentFile?.type === '3D' &&
      currentFile.cameraOptions?.fov === clampedFov &&
      (ongoing || !this.graph.hasOngoingAction())
    ) {
      return;
    }

    this.graph.wasmEdit((wasm) => wasm.setCameraFov(this.nodeId, clampedFov), ongoing);
  }

  setDimensions({ width, height }: Dimensions) {
    const clampedWidth = clampValue(Math.round(width), MIN_DIMENSION, MAX_DIMENSION);
    const clampedHeight = clampValue(Math.round(height), MIN_DIMENSION, MAX_DIMENSION);

    const nodeData = this.getNodeData();
    const currentFile = this.getCurrentFile();
    const viewport = currentFile?.type === '3D' ? currentFile.cameraOptions?.viewport : undefined;
    if (
      (viewport?.width ?? nodeData.width ?? DEFAULT_DIMENSION) === clampedWidth &&
      (viewport?.height ?? nodeData.height ?? DEFAULT_DIMENSION) === clampedHeight
    ) {
      return;
    }

    this.graph.wasmEdit((wasm) => wasm.setDimensions(this.nodeId, clampedWidth, clampedHeight));
  }

  resetCamera() {
    this.graph.wasm.call((wasm) => wasm.resetCamera(this.nodeId));
  }
}
